import { FC, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Folder, File, ExternalLink, Trash2 } from "lucide-react"
import { formatDistanceToNow } from "date-fns"
import { CaseListItem } from "@/types"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { DeleteCaseDialog } from "./DeleteCaseDialog"

interface CaseCardProps {
	caseData: CaseListItem
}

export const CaseCard: FC<CaseCardProps> = ({ caseData }) => {
	const navigate = useNavigate()
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

	return (
		<>
			<Card 
				className="w-full cursor-pointer group"
				onClick={() => navigate(`/cases/${caseData.id}`)}
			>
				<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
					<div className="flex items-center space-x-3">
						<div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
							<Folder className="h-5 w-5" />
						</div>
						<div>
							<CardTitle className="text-sm font-medium group-hover:text-primary transition-colors">
								{caseData.case_name}
							</CardTitle>
							<p className="text-xs text-muted-foreground">
								{formatDistanceToNow(caseData.created_at, { addSuffix: true })}
							</p>
						</div>
					</div>
					<div className="flex items-center space-x-2">
						<div className="opacity-0 group-hover:opacity-100 transition-opacity">
							<ExternalLink className="h-4 w-4 text-muted-foreground" />
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={(e) => {
								e.stopPropagation()
								setIsDeleteDialogOpen(true)
							}}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-2">
							<File className="h-4 w-4 text-muted-foreground" />
							<span className="text-xs text-muted-foreground">
								{caseData.document_count} documents
							</span>
						</div>
						<div className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
							{caseData.plaintiff_first_name} {caseData.plaintiff_last_name}
						</div>
					</div>
				</CardContent>
			</Card>

			<DeleteCaseDialog
				caseId={caseData.id}
				caseName={caseData.case_name}
				isOpen={isDeleteDialogOpen}
				onClose={() => setIsDeleteDialogOpen(false)}
			/>
		</>
	)
}
